import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { concatMap, filter, finalize, forkJoin, from, map, Observable, of, switchMap, tap, toArray } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BoardContactService } from '../../../../services/board-contact.service';
import { DialogService } from '../../../../services/dialog.service';
import { NotificationService } from '../../../../services/notifications.service';
import { BoardContact } from '../../board-v3.types';
import {
  formatContactPhone,
  getContactEmails,
  getContactErrorKey,
  getContactName,
  getContactPhones,
  getDuplicateContact,
} from '../contact.utils';
import {
  ContactPickerSlot,
  createEmptyContactSlot,
  getContactDraftPayload,
  getContactUpdatePayload,
} from '../contact-picker/contact-picker.component';
import { AddressInput } from '../../../../interfaces/card';
import { formatAddress, getAddressMapUrl } from '../../address.utils';

/** A slot that failed to save, to offer linking the duplicate it ran into */
class ContactSlotSaveError {
  constructor(
    readonly index: number,
    readonly error: unknown,
  ) {}
}

/** Contacts block of the card detail: all linked contacts are edited at once, like in the quick create form */
@Component({
  selector: 'app-board-v3-card-contacts',
  templateUrl: './card-contacts.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardContactsComponent {
  @Input({ required: true }) cardId!: string;
  @Input() contacts: BoardContact[] = [];
  @Output() contactsChange = new EventEmitter<BoardContact[]>();

  private readonly contactService = inject(BoardContactService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected busy = false;
  /** Slots of the edit mode, `null` while viewing */
  protected slots: ContactPickerSlot[] | null = null;

  protected readonly getContactName = getContactName;
  protected readonly getContactPhones = getContactPhones;
  protected readonly getContactEmails = getContactEmails;
  protected readonly formatContactPhone = formatContactPhone;
  protected readonly getAddressMapUrl = getAddressMapUrl;

  protected formatAddress(address: AddressInput): string {
    return formatAddress(address, String(this.translateService.instant('address_apartment_short')));
  }

  protected phoneHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, '')}`;
  }

  protected get pickedContactIds(): string[] {
    return (this.slots ?? []).map((slot) => slot.existing?.id).filter((id): id is string => !!id);
  }

  protected startEdit(): void {
    if (this.busy || this.slots) return;

    // Collapsed to a row each; a contact's form opens with its edit button
    this.slots = this.contacts.length
      ? this.contacts.map((contact) => ({ existing: contact, draft: null }))
      : [createEmptyContactSlot()];
  }

  protected cancelEdit(): void {
    if (!this.busy) this.slots = null;
  }

  protected addSlot(): void {
    if (this.slots) this.slots = [...this.slots, createEmptyContactSlot()];
  }

  protected setSlot(index: number, slot: ContactPickerSlot): void {
    if (this.slots) this.slots = this.slots.map((item, itemIndex) => (itemIndex === index ? slot : item));
  }

  protected removeSlot(index: number): void {
    if (this.slots) this.slots = this.slots.filter((_, itemIndex) => itemIndex !== index);
  }

  /** Saves every slot in order, then unlinks the contacts that were removed from the card */
  protected save(): void {
    const slots = this.slots;
    if (!slots || this.busy) return;

    slots.forEach((slot) => slot.draft?.markAllAsTouched());
    if (slots.some((slot) => slot.draft?.invalid)) return;

    this.busy = true;
    from(slots.map((slot, index) => ({ slot, index })))
      .pipe(
        filter(({ slot }) => !!slot.existing || !!slot.draft),
        concatMap(({ slot, index }) =>
          this.saveSlot(slot).pipe(
            // A saved slot is not saved again when the save is retried after a duplicate
            tap((contact) => this.setSlot(index, { existing: contact, draft: null })),
            catchError((error) => {
              throw new ContactSlotSaveError(index, error);
            }),
          ),
        ),
        toArray(),
        switchMap((saved) => {
          const contacts = saved.filter((contact, index) => saved.findIndex((item) => item.id === contact.id) === index);
          const removed = this.contacts.filter((contact) => !contacts.some((item) => item.id === contact.id));
          const unlinks = removed.map((contact) => this.contactService.unlinkCard(contact.id, this.cardId));

          const unlinked$: Observable<unknown> = unlinks.length ? forkJoin(unlinks) : of(null);
          return unlinked$.pipe(map(() => contacts));
        }),
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (contacts) => {
          this.slots = null;
          this.contactsChange.emit(contacts);
        },
        error: (error) => this.handleSaveError(error),
      });
  }

  protected openContact(contact: BoardContact): void {
    this.contactService
      .openContactDetail(contact.id, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.deleted) {
          this.remove(contact.id);
          return;
        }
        if (!result?.contact) return;

        const stillLinked = result.contact.cards?.some((card) => card.id === this.cardId) ?? true;
        if (stillLinked) this.upsert(result.contact);
        else this.remove(contact.id);
      });
  }

  protected unlink(contact: BoardContact): void {
    if (this.busy) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_contact_unlink_confirm', { name: getContactName(contact) }),
        isTranslate: true,
      })
      .pipe(
        filter(Boolean),
        switchMap(() => {
          this.busy = true;
          return this.contactService.unlinkCard(contact.id, this.cardId).pipe(finalize(() => (this.busy = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.remove(contact.id),
        error: (error) => this.showError(error),
      });
  }

  /** Updates the picked contact when edited or creates a new one, and links the result to the card */
  private saveSlot(slot: ContactPickerSlot): Observable<BoardContact> {
    const { existing, draft } = slot;

    if (!existing) {
      return this.contactService.createContact({ ...getContactDraftPayload(draft!), card_ids: [this.cardId] });
    }

    const updated$ = draft?.dirty
      ? this.contactService.updateContact(existing.id, getContactUpdatePayload(draft))
      : of(existing);
    const isLinked = this.contacts.some((contact) => contact.id === existing.id);

    return isLinked
      ? updated$
      : updated$.pipe(switchMap(() => this.contactService.linkCards(existing.id, [this.cardId])));
  }

  /** A new contact repeats a phone or an email of an existing one: offer to pick that one instead */
  private handleSaveError(failure: unknown): void {
    const { index, error } =
      failure instanceof ContactSlotSaveError ? failure : new ContactSlotSaveError(-1, failure);
    const slot = this.slots?.[index];
    const duplicate = getDuplicateContact(error);

    if (!slot || !duplicate || slot.existing || this.pickedContactIds.includes(duplicate.id)) {
      if (duplicate) {
        const message = this.translateService.instant('board_v3_contact_duplicate', { name: getContactName(duplicate) });
        this.notificationService.showError('', String(message));
      } else {
        this.showError(error);
      }
      return;
    }

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_contact_duplicate_link_confirm', {
          name: getContactName(duplicate),
        }),
        isTranslate: true,
      })
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setSlot(index, { existing: duplicate, draft: null });
        this.save();
      });
  }

  private upsert(contact: BoardContact): void {
    const exists = this.contacts.some((item) => item.id === contact.id);
    this.contactsChange.emit(
      exists ? this.contacts.map((item) => (item.id === contact.id ? contact : item)) : [...this.contacts, contact],
    );
  }

  private remove(contactId: string): void {
    this.contactsChange.emit(this.contacts.filter((item) => item.id !== contactId));
  }

  private showError(error: unknown): void {
    this.notificationService.showError('', String(this.translateService.instant(getContactErrorKey(error))));
  }
}
